import React, { useState, useEffect } from 'react';
import { database, auth } from '../firebase';
import { ref, push, update, serverTimestamp } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import PropTypes from 'prop-types';

const CustomerForm = ({ onSuccess, onCancel, customer }) => {
  const [formData, setFormData] = useState({
    visibility: true,
    customerName: "",
    under: "",
    underDetails: "",
    openingBalance: "",
    dueDays: "",
    creditLimit: "",
    dlNo: "",
    dlValidUpto: "",
    fssaiNo: "",
    fssaiValidUpto: "",
    contactPerson: "",
    address: "",
    address2: "",
    address3: "",
    city: "",
    zipCode: "",
    state: "Maharashtra",
    country: "India",
    phoneNo: "",
    mobileNo: "",
    faxNo: "",
    email: "",
    cstNo: "",
    gstTinNo: "",
    panNo: "",
    printName: "",
    obAmount: "",
    advanceAmount: "",
    debit: "",
    balanceAmount: "",
    smsAlert: "No",
    enableLoyalty: false,
    cardType: "",
    cardNumber: "",
    loyaltyPoints: "",
    redeemPoints: ""
  });

  const [currentUser, setCurrentUser] = useState(null);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  // Validation functions
  const validateCustomerName = (value) => {
    const regex = /^[A-Za-z\s]*$/;
    return regex.test(value);
  };

  const validateDigitsOnly = (value) => {
    const regex = /^\d*$/;
    return regex.test(value);
  };

  const validatePhoneNumber = (value) => {
    const regex = /^\d{0,10}$/;
    return regex.test(value);
  };

  const validateContactPerson = (value) => {
    const regex = /^[A-Za-z\s]*$/;
    return regex.test(value);
  };

  const validateCity = (value) => {
    const regex = /^[A-Za-z\s]*$/;
    return regex.test(value);
  };

  const validateZipCode = (value) => {
    const regex = /^\d{0,6}$/;
    return regex.test(value);
  };

  const validateDLNumber = (value) => {
    const regex = /^[A-Za-z0-9]*$/;
    return regex.test(value);
  };

  const validateFSSAINumber = (value) => {
    const regex = /^[A-Za-z\-\/]*$/;
    return regex.test(value);
  };

  const validateCSTNumber = (value) => {
    const regex = /^[A-Za-z\-\/]*$/;
    return regex.test(value);
  };

  const validateGSTTINNumber = (value) => {
    const regex = /^[A-Za-z0-9]{0,15}$/;
    return regex.test(value);
  };

  // Populate form with customer data if editing
  useEffect(() => {
    if (customer) {
      setFormData({
        ...formData,
        ...customer,
        visibility: customer.visibility !== undefined ? customer.visibility : true,
        underDetails: customer.underDetails || "",
        smsAlert: customer.smsAlert || "No",
        enableLoyalty: customer.enableLoyalty || false
      });
    }
  }, [customer]);

  // Fetch countries (only India)
  useEffect(() => {
    const fetchCountries = async () => {
      setLoading(true);
      try {
        const response = await fetch('https://restcountries.com/v3.1/name/india');
        if (!response.ok) throw new Error('Failed to fetch country data');
        const data = await response.json();
        const indiaCountry = data
          .filter(country => country.name.common === 'India')
          .map(country => ({
            name: country.name.common,
            code: country.cca2
          }));
        setCountries(indiaCountry);
      } catch (error) {
        console.error('Error fetching countries:', error);
        alert('Failed to load country data.');
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, []);

  // Fetch states when country is set
  useEffect(() => {
    const fetchStates = async () => {
      if (!formData.country) return;
      setLoading(true);
      try {
        const response = await fetch('https://countriesnow.space/api/v0.1/countries/states', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ country: formData.country })
        });
        if (!response.ok) throw new Error('Failed to fetch states');
        const data = await response.json();
        if (data.error) throw new Error(data.msg);
        setStates(data.data.states || []);
      } catch (error) {
        console.error('Error fetching states:', error);
        alert('Failed to load states.');
        setStates([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStates();
  }, [formData.country]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let isValid = true;
    let processedValue = value;

    switch (name) {
      case 'customerName':
        isValid = validateCustomerName(value);
        break;
      case 'zipCode':
        isValid = validateZipCode(value);
        break;
      case 'cardNumber':
        isValid = validateDigitsOnly(value);
        break;
      case 'phoneNo':
      case 'mobileNo':
        isValid = validatePhoneNumber(value);
        break;
      case 'contactPerson':
        isValid = validateContactPerson(value);
        break;
      case 'city':
        isValid = validateCity(value);
        break;
      case 'dlNo':
        isValid = validateDLNumber(value);
        break;
      case 'fssaiNo':
        isValid = validateFSSAINumber(value);
        break;
      case 'cstNo':
        isValid = validateCSTNumber(value);
        break;
      case 'gstTinNo':
        isValid = validateGSTTINNumber(value);
        break;
      default:
        isValid = true;
    }

    if (isValid) {
      setFormData({
        ...formData,
        [name]: type === "checkbox" ? checked : processedValue
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentUser) {
      alert("You must be logged in to add or edit a customer.");
      return;
    }

    if (!formData.customerName) {
      alert("Customer Name is required.");
      return;
    }

    if (formData.phoneNo && formData.phoneNo.length !== 10) {
      alert("Phone Number must be exactly 10 digits.");
      return;
    }

    if (formData.mobileNo && formData.mobileNo.length !== 10) {
      alert("Mobile Number must be exactly 10 digits.");
      return;
    }

    if (formData.zipCode && formData.zipCode.length !== 6) {
      alert("ZIP Code must be exactly 6 digits.");
      return;
    }

    if (formData.gstTinNo && formData.gstTinNo.length !== 15) {
      alert("GST TIN Number must be exactly 15 alphanumeric characters.");
      return;
    }

    try {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const customersRef = ref(database, `users/${encodedEmail}/customers`);
      
      const customerData = {
        ...formData,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email
      };

      if (customer && customer.id) {
        const customerRef = ref(database, `users/${encodedEmail}/customers/${customer.id}`);
        await update(customerRef, customerData);
        alert("Customer updated successfully!");
      } else {
        customerData.createdAt = serverTimestamp();
        customerData.createdBy = currentUser.email;
        await push(customersRef, customerData);
        alert("Customer added successfully!");
      }
      
      setFormData({
        visibility: true,
        customerName: "",
        under: "",
        underDetails: "",
        openingBalance: "",
        dueDays: "",
        creditLimit: "",
        dlNo: "",
        dlValidUpto: "",
        fssaiNo: "",
        fssaiValidUpto: "",
        contactPerson: "",
        address: "",
        address2: "",
        address3: "",
        city: "",
        zipCode: "",
        state: "Maharashtra",
        country: "India",
        phoneNo: "",
        mobileNo: "",
        faxNo: "",
        email: "",
        cstNo: "",
        gstTinNo: "",
        panNo: "",
        printName: "",
        obAmount: "",
        advanceAmount: "",
        debit: "",
        balanceAmount: "",
        smsAlert: "No",
        enableLoyalty: false,
        cardType: "",
        cardNumber: "",
        loyaltyPoints: "",
        redeemPoints: ""
      });

      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Error saving data:", error);
      alert("Error saving customer data: " + error.message);
    }
  };

  const handleCancel = () => {
    setFormData({
      visibility: true,
      customerName: "",
      under: "",
      underDetails: "",
      openingBalance: "",
      dueDays: "",
      creditLimit: "",
      dlNo: "",
      dlValidUpto: "",
      fssaiNo: "",
      fssaiValidUpto: "",
      contactPerson: "",
      address: "",
      address2: "",
      address3: "",
      city: "",
      zipCode: "",
      state: "Maharashtra",
      country: "India",
      phoneNo: "",
      mobileNo: "",
      faxNo: "",
      email: "",
      cstNo: "",
      gstTinNo: "",
      panNo: "",
      printName: "",
      obAmount: "",
      advanceAmount: "",
      debit: "",
      balanceAmount: "",
      smsAlert: "No",
      enableLoyalty: false,
      cardType: "",
      cardNumber: "",
      loyaltyPoints: "",
      redeemPoints: ""
    });
    if (onCancel) onCancel();
  };

  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '2rem auto',
      padding: '2rem',
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      minHeight: '100vh',
    },
    formWrapper: {
      backgroundColor: '#ffffff',
      borderRadius: '20px',
      padding: '2.5rem',
      border: '1px solid #e2e8f0',
    },
    heading: {
      fontSize: '2rem',
      fontWeight: '700',
      marginBottom: '2rem',
      color: '#1f2937',
      textAlign: 'center',
    },
    loadingContainer: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#6b7280',
    },
    loadingSpinner: {
      width: '3rem',
      height: '3rem',
      border: '4px solid #e5e7eb',
      borderTop: '4px solid #2563eb',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      margin: '0 auto 1rem',
    },
    formLayout: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '2rem',
    },
    twoColumnLayout: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: '2rem',
    },
    section: {
      backgroundColor: '#fff',
      padding: '1.5rem',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    },
    sectionHeading: {
      fontSize: '1.25rem',
      fontWeight: '600',
      marginBottom: '1rem',
      color: '#1f2937',
    },
    inputGroup: {
      marginBottom: '1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    inputGroup1: {
      marginBottom: '1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      width: '50%'
    },
    label: {
      display: 'block',
      fontSize: '1rem',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '0.25rem',
    },
    requiredAsterisk: {
      color: '#ef4444',
      marginLeft: '0.25rem',
    },
    input: {
      display: 'block',
      width: '90%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease',
    },
    emailInput: {
      display: 'block',
      width: '100%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease',
    },
    dlInput: {
      display: 'block',
      width: '90%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease',
    },
    select: {
      display: 'block',
      width: '100%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      backgroundColor: '#fff',
      cursor: 'pointer',
      appearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
      backgroundPosition: 'right 0.5rem center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '1em 1em',
      paddingRight: '2rem',
    },
    checkboxContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    checkbox: {
      width: '1rem',
      height: '1rem',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      cursor: 'pointer',
    },
    buttonContainer: {
      marginTop: '2rem',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      paddingTop: '1rem',
      borderTop: '1px solid #e2e8f0',
    },
    button: {
      padding: '0.5rem 1.5rem',
      borderRadius: '6px',
      fontWeight: '500',
      fontSize: '0.875rem',
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s ease',
    },
    submitButton: {
      backgroundColor: '#10b981',
      color: 'white',
    },
    cancelButton: {
      backgroundColor: '#ef4444',
      color: 'white',
    },
    gridTwoColumns: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '1rem',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.formWrapper}>
        <h1 style={styles.heading}>👤 {customer ? 'Edit Customer' : 'Add New Customer Entry'}</h1>

        {loading ? (
          <div style={styles.loadingContainer}>
            <div style={styles.loadingSpinner}></div>
            <p>Loading form data...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={styles.formLayout}>
              <div style={styles.twoColumnLayout}>
                <div style={styles.section}>
                  <h2 style={styles.sectionHeading}>📋 General Information</h2>
                  <div style={styles.inputGroup}>
                    <div style={styles.checkboxContainer}>
                      <input
                        type="checkbox"
                        name="visibility"
                        checked={formData.visibility}
                        onChange={handleChange}
                        style={styles.checkbox}
                      />
                      <label style={styles.label}>Visibility</label>
                    </div>
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>
                      Customer Name<span style={styles.requiredAsterisk}>*</span>
                    </label>
                    <input
                      type="text"
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="Enter customer name (letters and spaces only)"
                      required
                      title="Only letters and spaces are allowed"
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>
                      Group<span style={styles.requiredAsterisk}>*</span>
                    </label>
                    <select
                      name="under"
                      value={formData.under}
                      onChange={handleChange}
                      style={styles.select}
                      required
                    >
                      <option value="">Select Group</option>
                      <option value="CR">CR</option>
                      <option value="DB">DB</option>
                    </select>
                  </div>
                  <div style={styles.gridTwoColumns}>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Opening Balance</label>
                      <input
                        type="number"
                        name="openingBalance"
                        value={formData.openingBalance}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Due Days</label>
                      <input
                        type="number"
                        name="dueDays"
                        value={formData.dueDays}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder="0"
                        min="0"
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Credit Limit</label>
                      <input
                        type="number"
                        name="creditLimit"
                        value={formData.creditLimit}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>

                <div style={styles.section}>
                  <h2 style={styles.sectionHeading}>🪪 ID Proof</h2>
                  <div style={styles.gridTwoColumns}>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>DL No</label>
                      <input
                        type="text"
                        name="dlNo"
                        value={formData.dlNo}
                        onChange={handleChange}
                        style={styles.dlInput}
                        placeholder="Enter DL number (letters and numbers only)"
                        title="Only letters and numbers are allowed"
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>DL Valid Upto</label>
                      <input
                        type="date"
                        name="dlValidUpto"
                        value={formData.dlValidUpto}
                        onChange={handleChange}
                        style={styles.input}
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>FSSAI No</label>
                      <input
                        type="text"
                        name="fssaiNo"
                        value={formData.fssaiNo}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder="Enter FSSAI number (letters and symbols only)"
                        title="Only letters and special symbols (-/) are allowed"
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>FSSAI Valid Upto</label>
                      <input
                        type="date"
                        name="fssaiValidUpto"
                        value={formData.fssaiValidUpto}
                        onChange={handleChange}
                        style={styles.input}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div style={styles.twoColumnLayout}>
                <div style={styles.section}>
                  <h2 style={styles.sectionHeading}>📍 Contact Information</h2>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Contact Person</label>
                    <input
                      type="text"
                      name="contactPerson"
                      value={formData.contactPerson}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="Enter contact person (letters and spaces only)"
                      title="Only letters and spaces are allowed"
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Address</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="Enter address"
                    />
                  </div>
                  <div style={styles.gridTwoColumns}>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Address 2</label>
                      <input
                        type="text"
                        name="address2"
                        value={formData.address2}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder="Enter address 2"
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Address 3</label>
                      <input
                        type="text"
                        name="address3"
                        value={formData.address3}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder="Enter address 3"
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>City</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder="Enter city (letters and spaces only)"
                        title="Only letters and spaces are allowed"
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>ZIP Code</label>
                      <input
                        type="text"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder="Enter 6-digit ZIP code"
                        title="Exactly 6 digits are required"
                        maxLength={6}
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Country</label>
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        style={styles.select}
                        disabled={loading || countries.length === 0}
                      >
                        <option value="">Select Country</option>
                        {countries.map((country) => (
                          <option key={country.code} value={country.name}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>State</label>
                      <select
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        style={styles.select}
                        disabled={loading || !formData.country}
                      >
                        <option value="">Select State</option>
                        {states.map((state) => (
                          <option key={state.name} value={state.name}>
                            {state.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Phone No</label>
                      <input
                        type="text"
                        name="phoneNo"
                        value={formData.phoneNo}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder="Enter 10-digit phone number"
                        title="Exactly 10 digits are required"
                        maxLength={10}
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Mobile No</label>
                      <input
                        type="text"
                        name="mobileNo"
                        value={formData.mobileNo}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder="Enter 10-digit mobile number"
                        title="Exactly 10 digits are required"
                        maxLength={10}
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Fax No</label>
                      <input
                        type="text"
                        name="faxNo"
                        value={formData.faxNo}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder="Enter fax number"
                      />
                    </div>
                    <div style={styles.inputGroup1}>
                      <label style={styles.label}>E-Mail ID</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        style={ {display: 'block',
                          width: '200px',
                          padding: '0.5rem 0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '0.875rem',
                          backgroundColor: '#fff',
                          transition: 'border-color 0.2s ease',}}
                        placeholder="Enter email address"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <div style={{ ...styles.section, marginBottom: '2rem' }}>
                    <h2 style={styles.sectionHeading}>🏢 Company Information</h2>
                    <div style={styles.gridTwoColumns}>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>CST No</label>
                        <input
                          type="text"
                          name="cstNo"
                          value={formData.cstNo}
                          onChange={handleChange}
                          style={styles.input}
                          placeholder="Enter CST number (letters and symbols only)"
                          title="Only letters and special symbols (-/) are allowed"
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>GST TIN No</label>
                        <input
                          type="text"
                          name="gstTinNo"
                          value={formData.gstTinNo}
                          onChange={handleChange}
                          style={styles.input}
                          placeholder="Enter GST TIN No"
                          maxLength={15}
                          title="Exactly 15 alphanumeric characters are required"
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>PAN No</label>
                        <input
                          type="text"
                          name="panNo"
                          value={formData.panNo}
                          onChange={handleChange}
                          style={styles.input}
                          placeholder="Enter PAN number"
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Print Name</label>
                        <input
                          type="text"
                          name="printName"
                          value={formData.printName}
                          onChange={handleChange}
                          style={styles.input}
                          placeholder="Enter print name"
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>OB Amount</label>
                        <input
                          type="number"
                          name="obAmount"
                          value={formData.obAmount}
                          onChange={handleChange}
                          style={styles.input}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Advance Amount</label>
                        <input
                          type="number"
                          name="advanceAmount"
                          value={formData.advanceAmount}
                          onChange={handleChange}
                          style={styles.input}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Debit</label>
                        <input
                          type="number"
                          name="debit"
                          value={formData.debit}
                          onChange={handleChange}
                          style={styles.input}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Balance Amount</label>
                        <input
                          type="number"
                          name="balanceAmount"
                          value={formData.balanceAmount}
                          onChange={handleChange}
                          style={styles.input}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>SMS Alert</label>
                        <select
                          name="smsAlert"
                          value={formData.smsAlert}
                          onChange={handleChange}
                          style={styles.select}
                        >
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div style={styles.section}>
                    <h2 style={styles.sectionHeading}>🎁 Loyalty Details</h2>
                    <div style={styles.inputGroup}>
                      <div style={styles.checkboxContainer}>
                        <input
                          type="checkbox"
                          name="enableLoyalty"
                          checked={formData.enableLoyalty}
                          onChange={handleChange}
                          style={styles.checkbox}
                        />
                        <label style={styles.label}>Enable Loyalty</label>
                      </div>
                    </div>
                    <div style={styles.gridTwoColumns}>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Card Type</label>
                        <select
                          name="cardType"
                          value={formData.cardType}
                          onChange={handleChange}
                          style={styles.select}
                        >
                          <option value="">Select Card Type</option>
                          <option value="Gold">Gold</option>
                          <option value="Silver">Silver</option>
                        </select>
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Card Number</label>
                        <input
                          type="text"
                          name="cardNumber"
                          value={formData.cardNumber}
                          onChange={handleChange}
                          style={styles.input}
                          placeholder="Enter card number (digits only)"
                          title="Only digits are allowed"
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Loyalty Points</label>
                        <input
                          type="number"
                          name="loyaltyPoints"
                          value={formData.loyaltyPoints}
                          onChange={handleChange}
                          style={styles.input}
                          placeholder="0"
                          min="0"
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Redeem Points</label>
                        <input
                          type="number"
                          name="redeemPoints"
                          value={formData.redeemPoints}
                          onChange={handleChange}
                          style={styles.input}
                          placeholder="0"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.buttonContainer}>
              <button
                type="button"
                onClick={handleCancel}
                style={{ ...styles.button, ...styles.cancelButton }}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{ ...styles.button, ...styles.submitButton }}
                disabled={loading}
              >
                {customer ? 'Update Customer' : 'Add Customer'}
              </button>
            </div>
          </form>
        )}
      </div>

      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        @media (max-width: 768px) {
          .twoColumnLayout {
            grid-template-columns: 1fr;
          }
          .gridTwoColumns {
            grid-template-columns: 1fr;
          }
          .buttonContainer {
            flex-direction: column;
          }
          .button {
            width: 100%;
            justify-content: center;
          }
        }
        input:focus,
        select:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
        }
        input:hover,
        select:hover {
          border-color: #a1a1aa;
        }
        button:hover:not(:disabled) {
          filter: brightness(90%);
        }
      `}</style>
    </div>
  );
};

CustomerForm.propTypes = {
  customer: PropTypes.shape({
    id: PropTypes.string,
    visibility: PropTypes.bool,
    customerName: PropTypes.string,
    group: PropTypes.string,
    under: PropTypes.string,
    underDetails: PropTypes.string,
    openingBalance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dueDays: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    creditLimit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dlNo: PropTypes.string,
    dlValidUpto: PropTypes.string,
    fssaiNo: PropTypes.string,
    fssaiValidUpto: PropTypes.string,
    contactPerson: PropTypes.string,
    address: PropTypes.string,
    address2: PropTypes.string,
    address3: PropTypes.string,
    city: PropTypes.string,
    zipCode: PropTypes.string,
    state: PropTypes.string,
    country: PropTypes.string,
    phoneNo: PropTypes.string,
    mobileNo: PropTypes.string,
    faxNo: PropTypes.string,
    email: PropTypes.string,
    cstNo: PropTypes.string,
    gstTinNo: PropTypes.string,
    panNo: PropTypes.string,
    printName: PropTypes.string,
    obAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    advanceAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    debit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    balanceAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    smsAlert: PropTypes.string,
    enableLoyalty: PropTypes.bool,
    cardType: PropTypes.string,
    cardNumber: PropTypes.string,
    loyaltyPoints: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    redeemPoints: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func
};

export default CustomerForm;